import i18n from 'i18next';
import type { ComponentType } from 'react';
import { isImportLockingProject } from '@/atoms/import';
import { snackbarAtom } from '@/atoms/snackbar';
import { jotaiStore } from '@/atoms/store';
import { versionAtom } from '@/atoms/version';
import { snackbarColor } from '@/enums';
import { type ConfirmEntry, type CustomEntry, clearStack, nextModalId, pushEntry, removeEntry } from './stack';
import type { ConfirmOptions, ModalDef, ModalProps, ModalsApi, OpenOptions } from './types';

const isReadOnly = () => jotaiStore.get(versionAtom) !== '' || isImportLockingProject();

let readOnlyToastInFlight = false;
const fireReadOnlySnackbar = () => {
    if (readOnlyToastInFlight) return;
    readOnlyToastInFlight = true;
    jotaiStore.set(snackbarAtom, {
        open: true,
        message: i18n.t(isImportLockingProject() ? 'readonly.import-blocked' : 'readonly.blocked'),
        severity: snackbarColor.warning,
        vertical: 'bottom',
        horizontal: 'center',
    });
    setTimeout(() => {
        readOnlyToastInFlight = false;
    }, 1500);
};

const captureTrigger = (): Element | null => {
    if (typeof document === 'undefined') return null;
    const el = document.activeElement;
    return el instanceof Element && el !== document.body ? el : null;
};

export const modals: ModalsApi = {
    confirm(opts: ConfirmOptions = {}) {
        if (!opts.bypassReadOnly && isReadOnly()) {
            fireReadOnlySnackbar();
            return Promise.resolve(false);
        }
        return new Promise<boolean>(resolve => {
            const entry: ConfirmEntry = {
                kind: 'confirm',
                id: nextModalId(),
                options: opts,
                resolve,
                pending: false,
                dismissOnNavigate: true,
                triggerElement: opts.restoreFocus === false ? null : captureTrigger(),
            };
            pushEntry(entry);
        });
    },

    open<P, R>(Component: ModalDef<P, R>, payload: P, options: OpenOptions = {}) {
        if (!options.bypassReadOnly && isReadOnly()) {
            fireReadOnlySnackbar();
            return Promise.resolve<R | undefined>(undefined);
        }
        return new Promise<R | undefined>(resolve => {
            const entry: CustomEntry = {
                kind: 'custom',
                id: nextModalId(),
                Component: Component as unknown as ComponentType<ModalProps<unknown> & Record<string, unknown>>,
                payload: (payload ?? {}) as Record<string, unknown>,
                resolve: resolve as (value: unknown) => void,
                pending: false,
                dismissOnNavigate: options.dismissOnNavigate ?? true,
                triggerElement: captureTrigger(),
            };
            pushEntry(entry);
        });
    },

    closeAll() {
        clearStack();
    },
};

export { removeEntry };
